import React, { useState } from 'react';
import SearchInput from "../../../components/search-input/search-input";
import { useGetInvoiceGroupsQuery, useGetInvoiceGroupDetailsQuery, useGetCompaniesQuery, useCreateInvoiceGroupMutation } from "../endpoints/billing.endpoints";
import StyledDropdown from 'src/components/styled-dropdown';
import InvoiceTableRefactored from '../components/invoice-table-refactored';

const InvoicesTab: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [isGeneratingInvoices, setIsGeneratingInvoices] = useState(false);

  // API queries
  const { data: invoiceGroupsData, refetch: refetchInvoiceGroups } = useGetInvoiceGroupsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { data: invoiceGroupDetails, isLoading: isLoadingInvoiceGroupDetails, isFetching: isFetchingInvoiceGroupDetails, refetch: refetchInvoiceGroupDetails } = useGetInvoiceGroupDetailsQuery(
    selectedGroupId,
    {
      skip: !selectedGroupId,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: companies } = useGetCompaniesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [createInvoiceGroup] = useCreateInvoiceGroupMutation();

  // Sort invoice groups by most recent first
  const sortedInvoiceGroups = (invoiceGroupsData || [])
    .slice()
    .sort((a, b) => {
      if (a.year !== b.year) {
        return b.year - a.year;
      }
      return b.month - a.month;
    });

  // Auto-select the most recent invoice group by default
  if (sortedInvoiceGroups.length > 0 && !selectedGroupId) {
    setSelectedGroupId(sortedInvoiceGroups[0].id);
  }

  const formatMonthYear = (month: number, year: number): string => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return `${monthNames[month - 1]} ${year}`;
  };

  // Create dropdown options with "New Month" as first option
  const getDropdownOptions = () => {

    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    const currentMonthGroup = sortedInvoiceGroups.filter(group => group.year === currentYear && group.month === currentMonth);


    const options = currentMonthGroup.length > 0 ? [] :[
      {
        value: '__create_new__',
        label: 'Create Invoice Group',
        subtitle: 'Add a new invoice group',
      },
    ];

    // Add existing invoice groups
    sortedInvoiceGroups.forEach(group => {
      options.push({
        value: group.id,
        label: formatMonthYear(group.month, group.year),
        subtitle: `${group.month}/${group.year}`, // Show partial ID as subtitle or something else someday
      });
    });

    return options;
  };

  // Handle dropdown selection
  const handleDropdownSelect = async(value: string) => {
    if (value === '__create_new__') {
      const today = new Date();
      const currentMonth = today.getMonth() + 1;
      const currentYear = today.getFullYear();

      const newInvoiceGroup = await createInvoiceGroup({
        month: currentMonth,
        year: currentYear,
        isInitial: true,
      }).unwrap();
      setSelectedGroupId(newInvoiceGroup.id);
      if (refetchInvoiceGroupDetails) {
        await refetchInvoiceGroupDetails();
      }
    } else {
      setSelectedGroupId(value);
    }
  };


  const handleGenerateInvoices = async(groupId: string) => {
    const invoiceGroup = sortedInvoiceGroups.find(group => group.id === groupId);
    if (!invoiceGroup) return;

    setIsGeneratingInvoices(true);

    try {
      // Trigger actual invoice generation with isInitial=false
      await createInvoiceGroup({
        month: invoiceGroup.month,
        year: invoiceGroup.year,
        isInitial: false,
      }).unwrap();
    } catch (error) {
      console.error('Failed to generate invoices:', error);
      setIsGeneratingInvoices(false);
    }
  };

  return (
    <div className="h-full flex flex-col space-y-4">

      <div className="">
        <div className="flex flex-row gap-4 w-full items-center justify-between pb-1">
          <div>
            <SearchInput
              placeholder="Search invoices..."
              value={searchValue}
              onChange={setSearchValue}
              debounceMs={500}
              className="w-96 rounded-full"
              inputClassName='bg-white rounded-full '
            />
          </div>
          <div className="flex items-center gap-2">
            <StyledDropdown
              options={getDropdownOptions()}
              selectedValue={selectedGroupId}
              onSelect={handleDropdownSelect}
              placeholder="Select Month"
              buttonVariant="primary"
              isLoading={!invoiceGroupsData}
              buttonClassName='rounded-full bg-gray-201 text-gray-700'
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto relative">
        <InvoiceTableRefactored
          searchValue={searchValue}
          selectedGroupId={selectedGroupId}
          invoiceGroupDetails={invoiceGroupDetails}
          isLoadingDetails={isLoadingInvoiceGroupDetails}
          isFetchingDetails={isFetchingInvoiceGroupDetails}
          refetchInvoiceGroups={refetchInvoiceGroupDetails}
          companies={companies}
          isGeneratingInvoices={isGeneratingInvoices}
          onGenerationComplete={() => setIsGeneratingInvoices(false)}
          // onOpenPreviewModal={handleOpenPreviewModal} // TODO: Remove this if we don't need i
          onGenerateInvoices={handleGenerateInvoices}
        />
      </div>
    </div>
  );
};

export default InvoicesTab;